import { buildCutPreviewInstructionDescriptor } from "../messages/exportMessages.js";
import { buildCutWorkshopInstructionDescriptor } from "../messages/workshopMessages.js";
import { getCutSimulationCaption } from "./cutSequence";
import { isUsableOffcut } from "./offcuts";

const TOLERANCE = 2;

export function rectKey(rect) {
  return [
    Math.round(rect.x),
    Math.round(rect.y),
    Math.round(rect.length),
    Math.round(rect.width),
  ].join(":");
}

function rectsMatch(left, right, tolerance = TOLERANCE) {
  return Math.abs(left.x - right.x) <= tolerance
    && Math.abs(left.y - right.y) <= tolerance
    && Math.abs(left.length - right.length) <= tolerance
    && Math.abs(left.width - right.width) <= tolerance;
}

function normalizePlacement(placement) {
  return {
    x: Number(placement.x),
    y: Number(placement.y),
    length: Number(placement.length),
    width: Number(placement.width),
    ...placement,
  };
}

function findMatchingPlacement(stock, placements, tolerance = TOLERANCE) {
  const normalizedPlacements = placements.map(normalizePlacement);

  const exact = normalizedPlacements.find((placement) => rectsMatch(stock, placement, tolerance));
  if (exact) return exact;

  return normalizedPlacements.find((placement) => {
    const contained = placement.x >= stock.x - tolerance
      && placement.y >= stock.y - tolerance
      && placement.x + placement.length <= stock.x + stock.length + tolerance
      && placement.y + placement.width <= stock.y + stock.width + tolerance;
    if (!contained) return false;

    const stockArea = stock.length * stock.width;
    const placementArea = placement.length * placement.width;
    if (!stockArea || !placementArea) return false;

    return placementArea / stockArea >= 0.96;
  });
}

function findApplicableStock(stocks, cut) {
  return stocks.find((stock) => {
    if (cut.axis === "vertical") {
      if (cut.position <= stock.x + TOLERANCE || cut.position >= stock.x + stock.length - TOLERANCE) {
        return false;
      }

      if (cut.kind === "strip-cross") return false;

      return cut.start >= stock.y - TOLERANCE
        && cut.end <= stock.y + stock.width + TOLERANCE
        && Math.abs((cut.end - cut.start) - stock.width) <= TOLERANCE + 1;
    }

    if (cut.position <= stock.y + TOLERANCE || cut.position >= stock.y + stock.width - TOLERANCE) {
      return false;
    }

    if (cut.kind === "strip-cross") {
      return cut.start >= stock.x - TOLERANCE
        && cut.end <= stock.x + stock.length + TOLERANCE
        && Math.abs(cut.start - stock.x) <= TOLERANCE + 1
        && Math.abs(cut.end - (stock.x + stock.length)) <= TOLERANCE + 1;
    }

    return cut.start >= stock.x - TOLERANCE
      && cut.end <= stock.x + stock.length + TOLERANCE
      && Math.abs((cut.end - cut.start) - stock.length) <= TOLERANCE + 1;
  });
}

function splitStock(stock, cut) {
  if (cut.axis === "vertical") {
    const leftLength = cut.position - stock.x;
    const rightLength = stock.x + stock.length - cut.position;
    const pieces = [];
    if (leftLength > TOLERANCE) {
      pieces.push({ x: stock.x, y: stock.y, length: leftLength, width: stock.width });
    }
    if (rightLength > TOLERANCE) {
      pieces.push({ x: cut.position, y: stock.y, length: rightLength, width: stock.width });
    }
    return pieces;
  }

  const topWidth = cut.position - stock.y;
  const bottomWidth = stock.y + stock.width - cut.position;
  const pieces = [];
  if (topWidth > TOLERANCE) {
    pieces.push({ x: stock.x, y: stock.y, length: stock.length, width: topWidth });
  }
  if (bottomWidth > TOLERANCE) {
    pieces.push({ x: stock.x, y: cut.position, length: stock.length, width: bottomWidth });
  }
  return pieces;
}

export function classifySimulationStockPieces(
  stocks,
  {
    placements = [],
    remainingCuts = [],
    minOffcutLength = 500,
    minOffcutWidth = 300,
  },
) {
  return stocks.map((stock) => {
    const placement = findMatchingPlacement(stock, placements);

    if (placement) {
      return {
        ...stock,
        kind: "panel",
        placement,
      };
    }

    const hasFutureCut = remainingCuts.some((cut) => findApplicableStock([stock], cut));
    if (hasFutureCut) {
      return {
        ...stock,
        kind: "stock",
      };
    }

    if (isUsableOffcut(stock, minOffcutLength, minOffcutWidth)) {
      return {
        ...stock,
        kind: "offcut",
        label: `${Math.round(stock.length)} × ${Math.round(stock.width)}`,
      };
    }

    return {
      ...stock,
      kind: "scrap",
      label: `${Math.round(stock.length)} × ${Math.round(stock.width)}`,
    };
  });
}

export function getCutPreviewInstruction(cut, boardLength, boardWidth) {
  return buildCutPreviewInstructionDescriptor(cut, boardLength, boardWidth);
}

export function getCutWorkshopInstruction(cut, stock, boardLength, boardWidth) {
  return buildCutWorkshopInstructionDescriptor(cut, stock, boardLength, boardWidth);
}

export function getActiveStockForSimulationStep(currentStep, steps, cuts) {
  if (!cuts.length) return null;

  const cutNumber = currentStep === 0 ? 1 : Math.min(currentStep, cuts.length);
  const stockStep = steps[cutNumber - 1];
  const cut = cuts[cutNumber - 1];
  if (!stockStep?.stocks || !cut) return null;

  return findApplicableStock(stockStep.stocks, cut);
}

export function getCutSimulatorFocus({
  cutNumber,
  steps,
  cuts,
  boardLength,
  boardWidth,
}) {
  if (cutNumber > cuts.length) {
    return {
      isComplete: true,
      cutNumber,
      focusCut: null,
      activeStock: null,
      instruction: getCutWorkshopInstruction(null, null, boardLength, boardWidth),
    };
  }

  const cut = cuts[cutNumber - 1];
  const stockStep = steps[cutNumber - 1];
  const activeStock = stockStep?.stocks
    ? findApplicableStock(stockStep.stocks, cut)
    : null;

  return {
    isComplete: false,
    cutNumber,
    focusCut: cut,
    activeStock,
    instruction: getCutWorkshopInstruction(cut, activeStock, boardLength, boardWidth),
  };
}

export function getSimulationBoardState({
  stepIndex,
  steps,
  cuts,
  placements,
  minOffcutLength = 500,
  minOffcutWidth = 300,
}) {
  const step = steps.find((item) => item.index === stepIndex) || steps[0];
  const stocks = step?.stocks || [];
  const remainingCuts = cuts.slice(stepIndex);
  const pieces = classifySimulationStockPieces(stocks, {
    placements,
    remainingCuts,
    minOffcutLength,
    minOffcutWidth,
  });

  const nextCut = cuts[stepIndex];
  const activeStock = nextCut ? findApplicableStock(stocks, nextCut) : null;

  return {
    pieces,
    activeStockKey: activeStock ? rectKey(activeStock) : null,
    stockCount: stocks.length,
  };
}

/**
 * Build step metadata for the cut simulator from an ordered cut sequence.
 */
export function buildCutSimulationSteps(cuts, boardLength, boardWidth, margin = 0) {
  const steps = [{
    index: 0,
    cut: null,
    caption: getCutSimulationCaption(null, boardLength, boardWidth),
    stocks: [{ x: margin, y: margin, length: boardLength, width: boardWidth }],
  }];

  let stocks = [{ x: margin, y: margin, length: boardLength, width: boardWidth }];

  for (let i = 0; i < cuts.length; i += 1) {
    const cut = cuts[i];
    const stock = findApplicableStock(stocks, cut);
    if (!stock) {
      steps.push({
        index: cut.number,
        cut,
        caption: getCutSimulationCaption(cut, boardLength, boardWidth),
        error: true,
      });
      break;
    }

    const stockIndex = stocks.findIndex((item) => rectKey(item) === rectKey(stock));
    const pieces = splitStock(stock, cut);
    stocks = [
      ...stocks.slice(0, stockIndex),
      ...stocks.slice(stockIndex + 1),
      ...pieces,
    ];

    steps.push({
      index: cut.number,
      cut,
      caption: getCutSimulationCaption(cut, boardLength, boardWidth),
      stocks: stocks.map((piece) => ({ ...piece })),
    });
  }

  return steps;
}
